// #region Dependencias
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { analytics } from 'services/firebase'
import { setDialogoAbierto } from 'store/slices/uiSlice'
import { createClientRequest } from 'store/thunks/clientsThunks'
import {
  checkPaymentRequest,
  makePaymentRequest
} from 'store/thunks/pagesThunks'
import {
  ACCION_ENLACE,
  ACCION_CORREO,
  ACCION_DESPLAZARSE,
  ACCION_VIDEO
} from 'utils/constants'
import { logEvent } from 'firebase/analytics'
import { colors } from 'components/Library/theme'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Flex,
  FlexItem,
  InputImagePreview,
  Text
} from 'components/Library'
import Dialog from 'components/Popups/Dialog'
import Hero from 'components/Hero'
import TimeLine from 'components/TimeLine'
import VideoHomePlayer from 'components/VideoHomePlayer'
import VideoPlayer from 'components/VideoPlayer'
import IconPicker from './IconPicker'
import LanguageSelector from './LanguageSelector'
import { useTranslation } from 'react-i18next'
import SubHero from './SubHero'
import TextSlider from './Library/TextSlider/TextSlider'
// #endregion

// #region Estilos
const MainContainer = styled.div`
  background-color: ${colors.white};
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.14),
    0px 3px 1px -2px rgba(0, 0, 0, 0.12), 0px 1px 5px 0px rgba(0, 0, 0, 0.2);
  font-family: ${({ fontFamily }) => fontFamily};
`

const StyledButtonFixed = styled(Button)`
  display: ${({ visible }) => (visible ? 'unset' : 'none')};
  right: 0;
  margin: 1rem;
  position: fixed;
  top: 0;
  z-index: 100;
`

const StyledFlexItem = styled(FlexItem).attrs((props) => ({
  color: props.color
}))`
  cursor: pointer;
  transition: color 0.15s;

  &:hover {
    color: ${(props) =>
      props.color === 'primary'
        ? colors.primary
        : props.color === 'secondary'
        ? colors.secondary
        : props.color === 'tertiary'
        ? colors.tertiary
        : props.color === 'dark'
        ? colors.text
        : props.color};
  }
`
// #endregion

/**
 * Componente que renderiza el contenido de la landing page
 * con la información de la página seleccionada.
 * @class
 * @module Pages
 * @param {Object} props - Propiedades del componente.
 * @param {boolean} props.modoEdicion - Indica si se está en modo de edición.
 * @param {Object} props.valores - Objeto con los valores de la página.
 * @param {Function} props.handleInputChange - Callback para actualizar los valores de un elemento de la página.
 */
const Landing = ({ modoEdicion = false, valores, handleInputChange }) => {
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()

  const [mostrarVideo, setMostrarVideo] = useState(false)
  const [video, setVideo] = useState('')

  const [searchParams, setSearchParams] = useSearchParams()

  /**
   * Hook useEffect para verificar el estado de pago según el parámetro "orden"
   * mediante el thunk
   * {@link checkPaymentRequest} una vez ha sido finalizado.
   */
  useEffect(() => {
    const ordenEncoded = searchParams.get('orden')
    if (ordenEncoded) {
      try {
        const orden = JSON.parse(window.atob(ordenEncoded))
        if (orden.id) {
          setTimeout(() => {
            dispatch(checkPaymentRequest(orden, setSearchParams))
          }, 500)
        }
      } catch (error) {
        console.error(`No se pudo validar la orden ${ordenEncoded}`, error)
      }
    }
  }, [])

  /**
   * Hook useEffect para establecer el titulo de la pagina en el navegador
   * cuando se modifica el titulo en el editor o el idioma de la aplicación.
   */
  useEffect(() => {
    if (modoEdicion) {
      const titulo = valores.opciones[i18n.language].titulo
      document.title = titulo
    }
  }, [valores.opciones[i18n.language].titulo])

  const [mostrarBotonFixed, setMostrarBotonFixed] = useState(false)

  useEffect(() => {
    if (!modoEdicion) {
      const botonFijo = document.getElementById('main-button')

      const handleIntersection = (entries) => {
        entries.forEach((entry) => {
          setMostrarBotonFixed(!entry.isIntersecting)
        })
      }

      const observer = new IntersectionObserver(handleIntersection)
      if (botonFijo) observer.observe(botonFijo)

      return () => {
        if (botonFijo) observer.unobserve(botonFijo)
      }
    }
  }, [])

  /**
   * Función que maneja el call-to-action principal y secundario de la página dependiendo de las opciones seleccionadas.
   * @param {string} seccion - Sección de la página en la que se ejecuta el Call-to-action. (main, footer).
   */
  const handleCallToAction = (seccion, index = 0) => {
    if (modoEdicion) return

    logEvent(analytics, 'call_to_action', {
      tipo_cta: seccion === 'beneficios' ? `${seccion}-${index}` : seccion
    })

    const valor =
      seccion === 'main'
        ? valores.secciones.main
        : seccion === 'footer'
          ? valores.secciones.footer
          : seccion === 'beneficios'
            ? valores.secciones.beneficios.items[index]
            : ''

    const accion =
      seccion === 'beneficios'
        ? valores.secciones.beneficios.accion
        : valor.accion

    /**
     * Call-to-action que registra el correo electrónico del usuario y lo vincula
     * a la página seleccionada mediante el thunk {@link createClientRequest}
     */
    if (seccion !== 'beneficios' && +accion === ACCION_CORREO.value) {
      dispatch(
        setDialogoAbierto({
          abierto: true,
          tipo: 'email',
          titulo: valor[i18n.language].textoAccion,
          mensaje: t('actions.enter-email-suscribe'),
          callback: (cliente) =>
            dispatch(createClientRequest(cliente, i18n.language, t))
        })
      )
    }

    /**
     * Call-to-action para desplazarse a una sección de la página
     */
    if (+accion === ACCION_DESPLAZARSE.value) {
      document
        .getElementById(valor[i18n.language].keyAccion)
        .scrollIntoView({ behavior: 'smooth' })
    }

    /**
     * Call-to-action que abre un enlace externo pre-establecido.
     */
    if (+accion === ACCION_ENLACE.value) {
      window.open(valor[i18n.language].keyAccion)
    }

    /**
     * Call-to-action que abre un popup para visualizar un video de YouTube pre-establecido.
     */
    if (+accion === ACCION_VIDEO.value) {
      setVideo(valor[i18n.language].keyAccion)
      setMostrarVideo(true)
    }
  }

  /**
   * Función que maneja los pagos de plan o producto mediante el thunk {@link makePaymentRequest}.
   */
  const handlePurchase = () => {
    dispatch(
      setDialogoAbierto({
        abierto: true,
        tipo: 'payment',
        titulo: t('actions.make-payment'),
        mensaje: t('actions.enter-data-payment'),
        callback: (correo) => dispatch(makePaymentRequest(correo))
      })
    )
  }

  const [mostrarPagina, setMostrarPagina] = useState(false)

  /**
   * Hook useEffect para registrar el evento de visualización de página
   * en Google Analytics
   */
  useEffect(() => {
    if (valores.id) {
      setMostrarPagina(true)
      logEvent(analytics, 'screen_view', {
        firebase_screen: valores.id,
        modo_edicion: modoEdicion,
        idioma: i18n.language
      })
    }
  }, [i18n.language, modoEdicion, valores.id])

  if (mostrarPagina) {
    return (
      <MainContainer
        fontFamily={valores.opciones.fuente.nombre}
        style={{ position: 'relative' }}
      >
        <link href={valores.opciones.fuente.url} rel="stylesheet" />
        <LanguageSelector
          idiomas={valores.idiomas}
          borderradius={valores.opciones.borde}
          color={valores.opciones.colores.primario}
          modoEdicion={modoEdicion}
        />
        <Dialog
          borderradius={valores.opciones.borde}
          color={valores.opciones.colores.primario}
        />
        <VideoPlayer
          borderradius={valores.opciones.borde}
          mostrarVideo={mostrarVideo}
          setMostrarVideo={setMostrarVideo}
          video={video}
        />
        <Hero
          idioma={i18n.language}
          src={valores.secciones.hero[i18n.language].imagen}
          alt="Hero"
          contentEditable={modoEdicion}
          suppressContentEditableWarning={true}
          handleInputChange={handleInputChange}
        >
          {valores.secciones.hero[i18n.language].texto}
        </Hero>
        <Flex id="main" justifyContent="space-between">
          {/* Flex style={{ height: "70vh" }}> */}
          <FlexItem
            flexDirection="column"
            justifyContent="center"
            style={{
              padding: '2rem 0',
              flexBasis: '50%'
            }}
          >
            <TextSlider
              tag="h2"
              texts={valores.secciones.main[i18n.language].titulos}
              align="center"
              weight="bold"
              style={{
                marginBottom: '1rem',
                padding: '0 1rem',
                fontSize: '2.5rem'
              }}
            />
            <Text
              align="center"
              contentEditable={modoEdicion}
              suppressContentEditableWarning={true}
              data-id={`secciones.main.${i18n.language}.texto`}
              onBlur={handleInputChange}
              size="large"
              style={{
                maxWidth: '600px',
                padding: '.5rem 1rem',
                fontSize: '1.5rem'
              }}
            >
              {valores.secciones.main[i18n.language].texto}
            </Text>
            <Button
              id="main-button"
              borderradius={valores.opciones.borde}
              color={valores.opciones.colores.primario}
              text={valores.secciones.main[i18n.language].textoAccion}
              onClick={() => handleCallToAction('main')}
              style={{
                marginTop: '2rem'
              }}
            />
            <StyledButtonFixed
              visible={mostrarBotonFixed}
              borderradius={valores.opciones.borde}
              color={valores.opciones.colores.primario}
              text={valores.secciones.main[i18n.language].textoAccion}
              onClick={() => handleCallToAction('main')}
            />
          </FlexItem>
          <FlexItem
            alignItems="center"
            justifyContent="center"
            style={{
              backgroundColor: colors.secondary,
              flexBasis: '50%',
              height: '100%',
              padding: 0,
              width: '100%'
            }}
          >
            {valores.secciones.main[i18n.language].imagen.includes(
              'youtube'
            ) && (
              <VideoHomePlayer
                mostrarVideo={true}
                video={valores.secciones.main[i18n.language].imagen}
              />
            )}
            {!valores.secciones.main[i18n.language].imagen.includes(
              'youtube'
            ) && (
              <InputImagePreview
                name={`secciones.main.${i18n.language}.imagen`}
                modoEdicion={modoEdicion}
                onChange={handleInputChange}
                src={valores.secciones.main[i18n.language].imagen}
              />
            )}
          </FlexItem>
        </Flex>
        <Flex
          id="beneficios"
          alignItems="flex-start"
          style={{ backgroundColor: colors.light, padding: '2rem 0' }}
        >
          {valores.secciones.beneficios.items.map((beneficio, index) => (
            <StyledFlexItem
              color={valores.opciones.colores.primario}
              key={index}
              flexBasis={100 / valores.secciones.beneficios.items.length}
              flexDirection="column"
              onClick={() => handleCallToAction('beneficios', index)}
            >
              <IconPicker
                modoEdicion={modoEdicion}
                color={valores.opciones.colores.primario}
                value={
                  valores.secciones.beneficios.items[index].icono ||
                  'FaRegCircle'
                }
                size="3rem"
                name={`secciones.beneficios.items.${index}.icono`}
                handleInputChange={handleInputChange}
              />
              <Text
                align="center"
                contentEditable={modoEdicion}
                suppressContentEditableWarning={true}
                data-id={`secciones.beneficios.items.${index}.${i18n.language}.titulo`}
                onBlur={handleInputChange}
                size="large"
                weight="bold"
              >
                {beneficio[i18n.language].titulo}
              </Text>
              <Text
                align="center"
                contentEditable={modoEdicion}
                suppressContentEditableWarning={true}
                data-id={`secciones.beneficios.items.${index}.${i18n.language}.descripcion`}
                onBlur={handleInputChange}
                style={{ marginTop: '1rem' }}
              >
                {beneficio[i18n.language].descripcion}
              </Text>
            </StyledFlexItem>
          ))}
        </Flex>
        <TimeLine
          idioma={i18n.language}
          borderradius={valores.opciones.borde}
          colores={valores.opciones.colores}
          valores={valores.secciones.timeline}
          contentEditable={modoEdicion}
          suppressContentEditableWarning={true}
          handleInputChange={handleInputChange}
        />
        {/* {valores.productos && valores.productos.length > 0 && ( */}
        {valores.opciones.seccionPagos && (
          <Container
            id="planes"
            style={{
              backgroundColor: colors.light,
              borderBottom: '1px solid rgba(0, 0, 0, 0.125)',
              padding: '2rem 0'
            }}
          >
            <Text
              align="center"
              color={valores.opciones.colores.primario}
              weight="bold"
              contentEditable={modoEdicion}
              suppressContentEditableWarning={true}
              data-id={`secciones.planes.${i18n.language}.titulo`}
              onBlur={handleInputChange}
              style={{
                fontSize: '2rem',
                marginBottom: '1rem'
              }}
            >
              {valores.secciones.planes[i18n.language].titulo}
            </Text>
            <Flex
              style={{
                border: 0
              }}
            >
              {/* {valores.productos.map((plan, index) => ( */}
              {valores.secciones.planes.items.map((plan, index) => (
                <FlexItem key={index} flexBasis={33}>
                  <Card
                    borderradius={valores.opciones.borde}
                    style={{
                      maxWidth: '350px',
                      width: '100%'
                    }}
                  >
                    <CardHeader color="" flexDirection="column">
                      <Text
                        align="center"
                        contentEditable={modoEdicion}
                        suppressContentEditableWarning={true}
                        data-id={`secciones.planes.items.${index}.${i18n.language}.nombre`}
                        onBlur={handleInputChange}
                        color={valores.opciones.colores.primario}
                        weight="bold"
                      >
                        {/* {valores.productos[index].name} */}
                        {plan[i18n.language].nombre}
                      </Text>
                      <Text
                        align="center"
                        contentEditable={modoEdicion}
                        suppressContentEditableWarning={true}
                        data-id={`secciones.planes.items.${index}.precio`}
                        onBlur={handleInputChange}
                        size="large"
                        weight="bold"
                        style={{
                          fontSize: '2rem',
                          marginTop: '.25rem'
                        }}
                      >
                        {/* {formatearPrecio(valores.productos[index].precios[0].unit_amount)} */}
                        {plan.precio}
                      </Text>
                    </CardHeader>
                    <CardBody>
                      {/* {valores.productos[index].description} */}
                      <Text
                        contentEditable={modoEdicion}
                        suppressContentEditableWarning={true}
                        data-id={`secciones.planes.items.${index}.${i18n.language}.descripcion`}
                        onBlur={handleInputChange}
                      >
                        {plan[i18n.language].descripcion}
                      </Text>
                    </CardBody>
                  </Card>
                </FlexItem>
              ))}
            </Flex>
            <Flex
              style={{
                border: 0,
                padding: '0 2rem'
              }}
            >
              <FlexItem flexBasis={80}>
                <Text
                  contentEditable={modoEdicion}
                  suppressContentEditableWarning={true}
                  data-id={`secciones.planes.${i18n.language}.descripcion`}
                  onBlur={handleInputChange}
                >
                  {valores.secciones.planes[i18n.language].descripcion}
                </Text>
              </FlexItem>
              <FlexItem flexBasis={20}>
                <Button
                  borderradius={valores.opciones.borde}
                  color={valores.opciones.colores.primario}
                  text={t('actions.payment-view')}
                  onClick={handlePurchase}
                />
              </FlexItem>
            </Flex>
          </Container>
        )}
        {/* )} */}
        <SubHero
          idioma={i18n.language}
          color={valores.opciones.colores.primario}
          src={valores.secciones.subhero.imagen}
          alt="SubHero"
          contentEditable={modoEdicion}
          suppressContentEditableWarning={true}
          handleInputChange={handleInputChange}
        >
          {valores.secciones.subhero[i18n.language].texto}
        </SubHero>
        <Flex
          id="footer"
          style={{
            flexDirection: 'column',
            padding: '2rem 1rem'
          }}
        >
          <Text
            align="center"
            color={valores.opciones.colores.primario}
            weight="bold"
            contentEditable={modoEdicion}
            suppressContentEditableWarning={true}
            data-id={`secciones.footer.${i18n.language}.titulo`}
            onBlur={handleInputChange}
            style={{
              fontSize: '2rem'
            }}
          >
            {valores.secciones.footer[i18n.language].titulo}
          </Text>
          <Text
            align="center"
            contentEditable={modoEdicion}
            suppressContentEditableWarning={true}
            data-id={`secciones.footer.${i18n.language}.descripcion`}
            onBlur={handleInputChange}
            style={{
              margin: '1rem'
            }}
          >
            {valores.secciones.footer[i18n.language].descripcion}
          </Text>
          <FlexItem>
            <Button
              id="btn-footer"
              borderradius={valores.opciones.borde}
              color={valores.opciones.colores.primario}
              text={valores.secciones.footer[i18n.language].textoAccion}
              onClick={() => handleCallToAction('footer')}
              style={{
                margin: '0 1rem'
              }}
            />
          </FlexItem>
        </Flex>
      </MainContainer>
    )
  }
}

export default Landing
